<template>
  <v-card class="fl-calendar-picker" color="primary" flat>
    <v-card-title class="white--text">
      <div v-if="datePreview" class="text-h5">
        {{ datePreview }}
      </div>
      <div v-else>-</div>
      <v-spacer />
      <Tooltip v-if="datePreview" top tooltipText="Reset">
        <template #activator>
          <v-btn
            class="calender-fab-btn rounded-pill pa-1"
            depressed
            text
            @click="resetDate"
          >
            <v-icon color="white"> mdi-restore </v-icon>
          </v-btn>
        </template>
      </Tooltip>
    </v-card-title>
    <v-card-text class="white px-2 py-1 text-center">
      <div class="d-flex align-center" v-if="selectionMode == 'date'">
        <Tooltip top tooltipText="Previous Month">
          <template #activator>
            <v-icon @click="prevMonth"> mdi-chevron-left </v-icon>
          </template>
        </Tooltip>
        <v-spacer></v-spacer>
        <Tooltip top tooltipText="Select Month">
          <template #activator>
            <span
              :class="{
                'Pointer underline-hover': selectionMode != 'month',
              }"
              @click="selectionMode = 'month'"
            >
              {{ months[currentMonth] }}
            </span>
          </template>
        </Tooltip>
        <span>&nbsp;&nbsp;</span>
        <Tooltip top tooltipText="Select Year">
          <template #activator>
            <span
              :class="{
                'Pointer underline-hover': selectionMode != 'year',
              }"
              @click="selectionMode = 'year'"
            >
              {{ currentYear }}
            </span>
          </template>
        </Tooltip>
        <v-spacer></v-spacer>
        <Tooltip top tooltipText="Next Month">
          <template #activator>
            <v-icon @click="nextMonth"> mdi-chevron-right </v-icon>
          </template>
        </Tooltip>
      </div>
      <span v-if="selectionMode == 'month'"> Select Month </span>
      <span v-if="selectionMode == 'year'"> Select Year </span>
    </v-card-text>
    <v-card-text @mouseleave="tempDate = null" class="white px-2 py-1">
      <table class="date-table" v-if="selectionMode == 'date'" id="dates">
        <tr class="days-header-row">
          <th class="day-head">Sun</th>
          <th class="day-head">Mon</th>
          <th class="day-head">Tue</th>
          <th class="day-head">Wed</th>
          <th class="day-head">Thu</th>
          <th class="day-head">Fri</th>
          <th class="day-head">Set</th>
        </tr>
        <tr
          v-for="(week, weekIndex) in dateList"
          :key="weekIndex"
          class="days-body-row"
        >
          <th
            v-for="(weekDay, weekDayIndex) in week"
            :key="`day_${weekDayIndex}`"
            class="day-head pa-1"
          >
            <slot
              v-if="$slots && $slots.date && weekDay"
              name="date"
              :data="weekDay"
              :selectDate="selectDate"
              :setTempDate="setTempDate"
              :isSelectedDate="isSelectedDate"
              :isDisabled="isDisabled"
            ></slot>
            <v-badge
              v-if="!($slots && $slots.date) && weekDay"
              :value="haveEvent(weekDay.formattedDate)"
              :content="getBadgeValue(weekDay.formattedDate)"
              :dot="isBadgeDot"
              bordered
              color="primary"
              overlap
            >
              <v-btn
                class="calender-fab-btn rounded-pill pa-1"
                depressed
                :outlined="
                  weekDay.formattedDate == todayDate && !isSelectedDate(weekDay)
                "
                :text="
                  weekDay.formattedDate != todayDate && !isSelectedDate(weekDay)
                "
                v-if="!($slots && $slots.date) && weekDay"
                :disabled="isDisabled(weekDay.ms)"
                @click="selectDate(weekDay._d)"
                @mouseenter="setTempDate(weekDay._d)"
                :color="
                  isSelectedDate(weekDay) || weekDay.formattedDate == todayDate
                    ? 'primary'
                    : ''
                "
              >
                <span>
                  {{ weekDay.date }}
                </span>
              </v-btn>
            </v-badge>
          </th>
        </tr>
      </table>
      <v-row class="ma-0" v-if="selectionMode == 'month'">
        <v-col
          v-for="(month, monthIndex) in months"
          :key="`m_${monthIndex}`"
          cols="4"
        >
          <v-btn
            :color="currentMonth == monthIndex ? 'primary' : ''"
            depressed
            :text="currentMonth != monthIndex"
            :outlined="currentMonth == monthIndex"
            block
            @click="selectedMonth(monthIndex)"
          >
            {{ month }}
          </v-btn>
        </v-col>
      </v-row>
      <v-list class="pa-0 date-selection-list" v-if="selectionMode == 'year'">
        <v-list-item-group mandatory v-model="currentYear" color="primary">
          <v-list-item
            v-for="(year, yearIndex) in years"
            :key="`m_${yearIndex}`"
            class="text-center"
            :value="year"
            @click="selectedYear(year)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ year }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatDate } from "@/plugins/filters";
import Tooltip from "@/components/BaseComponents/UpdrateTooltip.vue";

export default {
  name: "fl-calendar",
  components: {
    Tooltip,
  },
  props: {
    range: {
      required: false,
    },
    min: {
      required: false,
    },
    max: {
      required: false,
    },
    returnValue: {
      type: String,
      required: false,
    },
    eventsList: {
      default: () => ({}),
    },
  },
  data() {
    return {
      selectionMode: "date",
      dateList: [],
      currentMonth: null,
      currentYear: null,
      tempDate: null,
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      years: [],
      selectedDate: null,
      today: new Date(),
      datePreview: null,
    };
  },
  computed: {
    todayDate() {
      return formatDate(this.today);
    },
    isBadgeDot() {
      return Array.isArray(this.eventsList);
    },
  },
  watch: {
    currentMonth(val) {
      const data = {
        month: val,
        year: this.currentYear,
        monthString: this.months[val],
      };
      setTimeout(() => {
        this.$emit("monthChanged", data);
      }, 300);
    },
    currentYear(val) {
      this.$emit("yearChanged", val);
    },
  },
  methods: {
    refreshPreview() {
      if (Array.isArray(this.selectedDate)) {
        if (this.selectedDate.length) {
          if (this.selectedDate.length == 1) {
            this.datePreview = this.getPreviewFormattedDate(
              this.selectedDate[0]
            );
          } else {
            this.datePreview = `${this.getPreviewFormattedDate(
              this.selectedDate[0]
            )} to ${this.getPreviewFormattedDate(this.selectedDate[1])}`;
          }
        } else {
          this.datePreview = null;
        }
      } else {
        this.datePreview = this.getPreviewFormattedDate(this.selectedDate);
      }
    },
    resetDate() {
      if (!this.range) {
        this.selectedDate = null;
      } else {
        this.selectedDate = [];
      }
      this.$emit("input", this.selectedDate);
      this.refreshPreview();
    },
    haveEvent(date) {
      return typeof this.eventsList == "object"
        ? Array.isArray(this.eventsList)
          ? this.eventsList.includes(date)
          : date in this.eventsList
        : false;
    },
    getBadgeValue(date) {
      if (this.haveEvent(date))
        return typeof this.eventsList == "object" &&
          Array.isArray(this.eventsList)
          ? ""
          : this.eventsList[date];
    },
    getPreviewFormattedDate(date) {
      return `${date.date} ${date.formattedMonth} ${date.year}`;
    },
    isDisabled(ms) {
      if (!ms) return false;

      let min = new Date(this.min);
      let max = new Date(this.max);

      if (min != "Invalid Date") {
        min.setHours(0);
        min.setMinutes(0);
        min.setSeconds(0);
        min.setMilliseconds(0);
      }

      if (max != "Invalid Date") {
        max.setHours(23);
        max.setMinutes(59);
        max.setSeconds(59);
        min.setMilliseconds(999);
      }

      if (min != "Invalid Date" && max != "Invalid Date") {
        min = Date.parse(min);
        max = Date.parse(max);

        return min > ms || max < ms;
      }

      if (min != "Invalid Date") {
        min = Date.parse(min);
        return min > ms;
      }

      if (max != "Invalid Date") {
        max = Date.parse(max);
        return max < ms;
      }
    },
    isSelectedDate(data) {
      let date = data?.ms;
      if (!date) return false;
      if (!this.range) {
        return this.selectedDate?.ms == date;
      } else {
        if (Array.isArray(this.selectedDate)) {
          if (this.selectedDate.length == 2) {
            const d1 = this.selectedDate[0];
            const d2 = this.selectedDate[1];
            return d1.ms <= date && d2.ms >= date;
          } else if (this.selectedDate.length == 1) {
            if (!this.tempDate) {
              const d1 = this.selectedDate[0];
              return d1?.ms == date;
            } else {
              const d1 = this.selectedDate[0];
              const d2 = Date.parse(new Date(this.tempDate));

              return (
                (d1.ms <= d2 && d1.ms <= date && d2 >= date) ||
                (d1.ms >= d2 && d1.ms >= date && d2 <= date)
              );
            }
          }
        }
        return false;
      }
    },
    generateDates(__date = new Date()) {
      this.currentMonth = __date.getMonth();
      this.currentYear = __date.getFullYear();

      let cDate = new Date(this.currentYear, this.currentMonth, 1, 0, 0, 0);
      this.dateList = [];

      let weekDaysList = [];
      while (true) {
        const month = cDate.getMonth();

        const day = cDate.getDay();

        if (day == 0) {
          this.dateList.push(weekDaysList);
          weekDaysList = new Array(7);
        }

        if (month != this.currentMonth) {
          this.dateList.push(weekDaysList);
          weekDaysList = new Array(7);
          break;
        }

        const object = this.getDateObject(cDate);

        weekDaysList[day] = object;

        cDate.setMilliseconds(86400000);
      }
    },
    getDateObject(date = new Date()) {
      const dateObj = {
        date: date.getDate(),
        month: date.getMonth(),
        formattedMonth: this.months[date.getMonth()],
        year: date.getFullYear(),
        day: date.getDay(),
        _d: date.toString(),
        ms: Date.parse(date),
        formattedDate: formatDate(date),
      };

      return dateObj;
    },
    selectDate(date) {
      if (typeof date == "string") date = new Date(date);
      let dateObject;
      if (!this.range) {
        this.selectedDate = this.getDateObject(date);
        if (this.returnValue && this.returnValue in this.selectedDate) {
          dateObject = this.selectedDate[this.returnValue];
        } else {
          dateObject = { ...this.selectedDate };
        }
      } else {
        if (
          !this.selectedDate ||
          !Array.isArray(this.selectedDate) ||
          this.selectedDate.length == 2
        ) {
          this.selectedDate = [];
        }
        const d = this.getDateObject(date);
        if (
          this.selectedDate.length &&
          this.selectedDate[0].formattedDate > d.formattedDate
        ) {
          this.selectedDate.unshift(d);
        } else {
          this.selectedDate.push(d);
        }

        let i = 0;
        dateObject = [];
        while (i < this.selectedDate.length) {
          const __date = { ...this.selectedDate[i] };
          i++;

          if (this.returnValue && this.returnValue in __date) {
            dateObject.push(__date[this.returnValue]);
          } else {
            dateObject.push({ ...__date });
          }
        }
      }

      this.refreshPreview();
      this.$emit("input", dateObject);
    },
    nextMonth() {
      let date = new Date(this.currentYear, this.currentMonth, 31, 0, 0, 0);
      date.setMilliseconds(86400000);
      this.generateDates(date);
    },
    prevMonth() {
      let date = new Date(this.currentYear, this.currentMonth, 0, 0, 0, 0);
      date.setMilliseconds(-86400000);
      this.generateDates(date);
    },
    selectedMonth(month) {
      let date = new Date(this.currentYear, month, 1, 0, 0, 0);
      this.generateDates(date);
      this.selectionMode = "date";
    },
    selectedYear(year) {
      let date = new Date(year, this.currentMonth, 1, 0, 0, 0);
      this.generateDates(date);
      this.selectionMode = "month";
    },
    setTempDate(date) {
      if (Array.isArray(this.selectedDate) && this.selectedDate.length == 1) {
        this.tempDate = date;
      }
    },
    loadYears() {
      let year = 1970;

      while (year < 2050) {
        this.years.push(year);
        year++;
      }
    },
    loadInitialDate() {
      const date = new Date();
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      this.selectDate(date);
      date.setMilliseconds(86400000);
      this.selectDate(date);
    },
  },
  mounted() {
    this.loadYears();
    this.generateDates();
    // this.loadInitialDate();
  },
};
</script>

<style>
</style>